// TextCodeViewer.js - Fixed version with synchronized line numbers
import React, { useState, useEffect, useRef } from 'react';
import { Loader, AlertTriangle, Copy, Type, RotateCcw } from 'lucide-react';

const TextCodeViewer = ({ fileUrl, fileName, fileType }) => {
    const [textContent, setTextContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lineNumbers, setLineNumbers] = useState(true);
    const [fontSize, setFontSize] = useState(14);
    const [wordWrap, setWordWrap] = useState(false);
    const [copied, setCopied] = useState(false);
    
    // Refs for synchronized scrolling
    const lineNumbersRef = useRef(null);
    const contentRef = useRef(null);

    useEffect(() => {
        loadTextContent();
    }, [fileUrl]);

    const loadTextContent = async () => {
        try {
            setLoading(true);
            const response = await fetch(fileUrl);
            if (!response.ok) throw new Error('Failed to load file content');
            
            const text = await response.text();
            setTextContent(text);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const getLanguage = () => {
        const extension = fileName.split('.').pop()?.toLowerCase();
        const languageMap = {
            js: 'javascript', jsx: 'javascript', ts: 'typescript', tsx: 'typescript',
            py: 'python', rb: 'ruby', php: 'php', java: 'java', cpp: 'cpp', c: 'c',
            css: 'css', scss: 'css', sass: 'css', less: 'css',
            html: 'html', htm: 'html', xml: 'xml', svg: 'xml',
            json: 'json', yaml: 'yaml', yml: 'yaml', toml: 'toml',
            md: 'markdown', txt: 'text', sql: 'sql', sh: 'bash', bat: 'batch', lua:'lua'
        };
        return languageMap[extension] || 'text';
    };

    const applySyntaxHighlighting = (text, language) => {
        const patterns = {
            javascript: [
                { pattern: /\b(const|let|var|function|return|if|else|for|while|do|break|continue|switch|case|default|try|catch|finally|throw|async|await|class|extends|import|export|from|as)\b/g, className: 'keyword' },
                { pattern: /(["'`])((?:\\.|(?!\1)[^\\])*?)\1/g, className: 'string' },
                { pattern: /\/\*[\s\S]*?\*\/|\/\/.*$/gm, className: 'comment' },
                { pattern: /\b\d+\.?\d*\b/g, className: 'number' },
                { pattern: /\b(true|false|null|undefined)\b/g, className: 'keyword' },
            ],
            python: [
                { pattern: /\b(def|class|if|elif|else|for|while|return|import|from|as|try|except|finally|with|lambda|and|or|not|in|is|True|False|None)\b/g, className: 'keyword' },
                { pattern: /(["'])((?:\\.|(?!\1)[^\\])*?)\1/g, className: 'string' },
                { pattern: /#.*$/gm, className: 'comment' },
                { pattern: /\b\d+\.?\d*\b/g, className: 'number' },
            ],
            css: [
                { pattern: /([a-zA-Z-]+)\s*:/g, className: 'keyword' },
                { pattern: /(["'])((?:\\.|(?!\1)[^\\])*?)\1/g, className: 'string' },
                { pattern: /\/\*[\s\S]*?\*\//g, className: 'comment' },
                { pattern: /#[0-9a-fA-F]{3,6}\b/g, className: 'number' },
            ],
            json: [
                { pattern: /(["'])((?:\\.|(?!\1)[^\\])*?)\1:/g, className: 'keyword' },
                { pattern: /(["'])((?:\\.|(?!\1)[^\\])*?)\1/g, className: 'string' },
                { pattern: /\b\d+\.?\d*\b/g, className: 'number' },
                { pattern: /\b(true|false|null)\b/g, className: 'keyword' },
            ]
        };

        let highlightedText = text;
        const langPatterns = patterns[language] || [];

        langPatterns.forEach(({ pattern, className }) => {
            highlightedText = highlightedText.replace(pattern, (match) => {
                return `<span class="${className}">${match}</span>`;
            });
        });

        return highlightedText;
    };

    const formatJSON = (text) => {
        try {
            const parsed = JSON.parse(text);
            return JSON.stringify(parsed, null, 2);
        } catch (e) {
            return text;
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(textContent);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // CRITICAL FIX: Synchronized scrolling handler
    const handleContentScroll = (e) => {
        if (lineNumbersRef.current && contentRef.current) {
            // Sync the line numbers scroll position with content scroll
            lineNumbersRef.current.scrollTop = e.target.scrollTop;
        }
    };

    const renderLineNumbers = (text) => {
        const lines = text.split('\n');
        return (
            // <div 
            //     ref={lineNumbersRef}
            //     className="line-numbers-container"
            //     style={{
            //         fontSize: `${fontSize}px`,
            //         lineHeight: '1.5',
            //         padding: '1rem 0.75rem',
            //         textAlign: 'right',
            //         color: '#6b7280',
            //         fontFamily: 'Monaco, Menlo, Ubuntu Mono, Consolas, monospace',
            //         userSelect: 'none',
            //         background: '#1f2937',
            //         borderRight: '1px solid #374151',
            //         width: '4rem',
            //         minWidth: '4rem',
            //         maxWidth: '4rem',
            //         flexShrink: 0,
            //         overflow: 'hidden', // CRITICAL: Remove scrolling from line numbers
            //         pointerEvents: 'none' // Prevent interaction with line numbers
            //     }}
            // >
            //     {lines.map((_, index) => (
            //         <div 
            //             key={index} 
            //             style={{ 
            //                 height: `${fontSize * 1.5}px`,
            //                 display: 'flex',
            //                 alignItems: 'center',
            //                 justifyContent: 'flex-end'
            //             }}
            //         >
            //             {index + 1}
            //         </div>
            //     ))}
            // </div>
            null
        );
    };

    const renderContent = () => {
        const language = getLanguage();
        let processedText = textContent;
        
        if (language === 'json') {
            processedText = formatJSON(textContent);
        }

        const highlightedText = applySyntaxHighlighting(processedText, language);

        return (
            <div 
                ref={contentRef}
                className={`text-content-container ${wordWrap ? 'word-wrap' : ''}`}
                onScroll={handleContentScroll} // CRITICAL: Add scroll handler
                tabIndex={0} // Make it focusable for keyboard navigation
                style={{
                    fontSize: `${fontSize}px`,
                    lineHeight: '1.5',
                    fontFamily: 'Monaco, Menlo, Ubuntu Mono, Consolas, monospace',
                    flex: 1,
                    overflow: 'auto',
                    background: '#111827',
                    minWidth: 0
                }}
            >
                <pre 
                    style={{ 
                        fontSize: `${fontSize}px`,
                        lineHeight: '1.5',
                        margin: 0,
                        padding: '1rem',
                        minHeight: '100%',
                        width: wordWrap ? '100%' : 'max-content',
                        whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
                        wordBreak: wordWrap ? 'break-word' : 'normal'
                    }}
                >
                    <code 
                        dangerouslySetInnerHTML={{ __html: highlightedText }}
                        style={{ 
                            fontSize: `${fontSize}px`,
                            lineHeight: '1.5',
                            color: '#e5e7eb'
                        }}
                    />
                </pre>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="text-viewer-loading">
                <Loader className="h-8 w-8 animate-spin text-blue-400 mb-4" />
                <p className="text-gray-400">Loading file content...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-viewer-error">
                <AlertTriangle className="h-8 w-8 text-red-400 mb-4" />
                <p className="text-gray-400 mb-2">Failed to load file content</p>
                <p className="text-sm text-gray-500">{error}</p>
                <button 
                    onClick={() => loadTextContent()}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    const language = getLanguage();
    const lineCount = textContent.split('\n').length;
    const fileSize = new Blob([textContent]).size;

    return (
        <div className="text-code-viewer">
            {/* Enhanced Toolbar */}
            <div className="text-viewer-header flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-600">
                <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                        <div className="h-3 w-3 rounded-full bg-red-500"></div>
                        <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                        <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    </div>
                    <span className="text-gray-300 font-mono text-sm truncate max-w-xs">{fileName}</span>
                    <span className="text-xs text-white bg-blue-600 px-2 py-1 rounded font-semibold">
                        {language.toUpperCase()}
                    </span>
                </div>
                
                <div className="flex items-center space-x-3">
                    {/* Font size controls */}
                    <div className="flex items-center space-x-2 bg-gray-700 rounded-lg px-2 py-1">
                        <button
                            onClick={() => setFontSize(prev => Math.max(prev - 1, 10))}
                            className="text-gray-400 hover:text-white p-1 rounded transition-colors"
                            title="Decrease font size"
                            disabled={fontSize <= 10}
                        >
                            <span className="text-sm font-bold">A-</span>
                        </button>
                        <span className="text-xs text-gray-300 min-w-[2.5rem] text-center font-mono">
                            {fontSize}px
                        </span>
                        <button
                            onClick={() => setFontSize(prev => Math.min(prev + 1, 24))}
                            className="text-gray-400 hover:text-white p-1 rounded transition-colors"
                            title="Increase font size"
                            disabled={fontSize >= 24}
                        >
                            <span className="text-sm font-bold">A+</span>
                        </button>
                    </div>

                    {/* View options */}
                    <div className="flex items-center space-x-1">
                        <button
                            onClick={() => setLineNumbers(!lineNumbers)}
                            className={`px-3 py-1.5 text-xs rounded transition-colors font-medium ${
                                lineNumbers 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-gray-700 text-gray-300 hover:text-white hover:bg-gray-600'
                            }`}
                            title="Toggle line numbers"
                        >
                            #
                        </button>
                        
                        <button
                            onClick={() => setWordWrap(!wordWrap)}
                            className={`px-3 py-1.5 text-xs rounded transition-colors font-medium flex items-center space-x-1 ${
                                wordWrap 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-gray-700 text-gray-300 hover:text-white hover:bg-gray-600'
                            }`}
                            title="Toggle word wrap"
                        >
                            <Type className="h-3 w-3" />
                        </button>
                        
                        <button
                            onClick={handleCopy}
                            className={`px-3 py-1.5 text-xs rounded transition-colors font-medium flex items-center space-x-1 ${
                                copied 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-gray-700 text-gray-300 hover:text-white hover:bg-gray-600'
                            }`}
                            title="Copy to clipboard"
                        >
                            <Copy className="h-3 w-3" />
                            <span>{copied ? 'Copied!' : 'Copy'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Content area - CRITICAL FIX for synchronized scrolling */}
            <div className="text-viewer-content">
                {lineNumbers && renderLineNumbers(textContent)}
                {renderContent()}
            </div>

            {/* Enhanced Status bar */}
            <div className="text-viewer-footer px-4 py-2 bg-gray-800 border-t border-gray-600 flex justify-between items-center text-xs text-gray-400 font-mono">
                <div className="flex items-center space-x-4">
                    <span className="flex items-center space-x-1">
                        <span className="text-gray-500">Encoding:</span>
                        <span className="text-gray-300">UTF-8</span>
                    </span>
                    <span className="flex items-center space-x-1">
                        <span className="text-gray-500">Size:</span>
                        <span className="text-gray-300">{(fileSize / 1024).toFixed(1)} KB</span>
                    </span>
                </div>
                <div className="flex items-center space-x-4">
                    <span className="flex items-center space-x-1">
                        <span className="text-gray-500">Lines:</span>
                        <span className="text-gray-300">{lineCount.toLocaleString()}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                        <span className="text-gray-500">Characters:</span>
                        <span className="text-gray-300">{textContent.length.toLocaleString()}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                        <span className="text-gray-500">Language:</span>
                        <span className="text-gray-300">{language}</span>
                    </span>
                </div>
            </div>
        </div>
    );
};

export default TextCodeViewer;